// ============================================
// Digital Signs - React Hooks
// ============================================

import { useState, useEffect, useCallback } from 'react';
import { displaysApi, subAccountsApi, locationsApi, lookupsApi } from './api';

// Hook to load all app data
export function useAppData() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    displays: [],
    subAccounts: [],
    locations: [],
    lookups: null,
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [displays, subAccounts, locations, lookups] = await Promise.all([
        displaysApi.list().catch(() => []),
        subAccountsApi.list().catch(() => []),
        locationsApi.list().catch(() => []),
        lookupsApi.getAll().catch(() => null),
      ]);
      
      setData({ displays, subAccounts, locations, lookups });
    } catch (err) {
      setError(err.message);
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return { ...data, loading, error, refresh: loadData };
}

// Hook for displays management
export function useDisplays() {
  const [displays, setDisplays] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await displaysApi.list();
      setDisplays(data || []);
    } catch (err) {
      console.error('Failed to load displays:', err);
    } finally {
      setLoading(false);
    }
  };

  const create = async (displayData) => {
    const result = await displaysApi.create(displayData);
    await load();
    return result;
  };

  const update = async (id, displayData) => {
    const result = await displaysApi.update(id, displayData);
    await load();
    return result;
  };

  const remove = async (id) => {
    await displaysApi.delete(id);
    await load();
  };

  const regeneratePin = async (id) => {
    const result = await displaysApi.regeneratePin(id);
    await load();
    return result;
  };

  useEffect(() => { load(); }, []);

  return { displays, loading, load, create, update, remove, regeneratePin };
}

// Hook for sub-accounts management
export function useSubAccounts() {
  const [subAccounts, setSubAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await subAccountsApi.list();
      setSubAccounts(data || []);
    } catch (err) {
      console.error('Failed to load sub-accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const create = async (accountData) => {
    const result = await subAccountsApi.create(accountData);
    await load();
    return result;
  };

  const update = async (id, accountData) => {
    const result = await subAccountsApi.update(id, accountData);
    await load();
    return result;
  };

  const remove = async (id) => {
    await subAccountsApi.delete(id);
    await load();
  };

  useEffect(() => { load(); }, []);

  return { subAccounts, loading, load, create, update, remove };
}

// Hook for locations management  
export function useLocations() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await locationsApi.list();
      setLocations(data || []);
    } catch (err) {
      console.error('Failed to load locations:', err);
    } finally {
      setLoading(false);
    }
  };

  const create = async (locationData) => {
    const result = await locationsApi.create(locationData);
    await load();
    return result;
  };

  const update = async (id, locationData) => {
    const result = await locationsApi.update(id, locationData);
    await load();
    return result;
  };

  const remove = async (id) => {
    await locationsApi.delete(id);
    await load();
  };

  useEffect(() => { load(); }, []);

  return { locations, loading, load, create, update, remove };
}

// Hook for lookups (static data)
export function useLookups() {
  const [lookups, setLookups] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    lookupsApi.getAll()
      .then(setLookups)
      .catch(err => console.error('Failed to load lookups:', err))
      .finally(() => setLoading(false));
  }, []);

  return { lookups, loading };
}
